import { AiService } from "@ms/shared-components";
import type { ThinkingMode } from "@ms/shared-components/comps/CompsAi";
import { aiConfigApi } from "../../../service/aiConfigApi";

/**
 * AI 服务配置接口
 */
export interface AiServiceConfig {
  apiKey: string;
  model: string;
  endpoint: string;
  qwenParams: {
    temperature: number;
    max_tokens: number;
    top_p: number;
  };
  useQwen: boolean;
  headers: {
    "Content-Type": string;
  };
}

/**
 * AI 调用参数
 */
export interface CallAiParams {
  data: any[];
  thinkingMode: ThinkingMode;
}

/**
 * 初始化 AI 服务配置
 */
export const initializeAiService = (): void => {
  console.log("🔧 [AI服务] 初始化配置...");

  // 判断是否为开发环境
  const isDevelopment = process.env.NODE_ENV === "development";

  // 开发环境使用代理端点，生产环境直接使用阿里云端点
  // 注意：生产环境需要通过后端代理或 nginx 配置代理
  const endpoint = isDevelopment
    ? "/api/qwen" // 开发环境：使用 webpack devServer 代理
    : "https://dashscope.aliyuncs.com/api/v1/services/aigc/text-generation/generation"; // 生产环境：直接调用（需要后端代理）

  console.log("🔧 [AI服务] 环境:", {
    isDevelopment,
    endpoint,
  });

  // 配置 Qwen API
  AiService.setConfig({
    // Qwen API Key
    apiKey: "sk-79bb1c54a76d4fbba359a4f49060873a",

    // 模型选择（默认 qwen-turbo）
    model: "qwen-turbo",

    // Qwen API 端点
    endpoint: endpoint,

    // Qwen API 参数
    qwenParams: {
      temperature: 0.7, // 温度参数，控制随机性（0-2）
      max_tokens: 2000, // 最大生成 token 数
      top_p: 0.9, // Top-p 采样（0-1）
    },

    // 使用 Qwen API（默认 true）
    useQwen: true,

    // 请求头（可选，会自动添加 Authorization）
    headers: {
      "Content-Type": "application/json",
    },
  });

  console.log("✅ [AI服务] 配置完成", {
    model: "qwen-turbo",
    endpoint: endpoint,
    hasApiKey: !!AiService.getConfig()?.apiKey,
    isDevelopment: isDevelopment,
  });

  // 测试连接（可选，用于验证配置）
  // 注意：这里不实际调用，只是验证配置是否正确设置
  const config = AiService.getConfig();
  if (!config?.apiKey) {
    console.error("❌ [AI服务] API Key 未设置");
  }
};

/**
 * 从树形数据中提取 component_id
 * 树形数据的第一层节点通常包含 component_id 信息
 * 节点可能包含 dataSource 属性，其中包含 id 字段（如 "stat-card:price-adjustment-stores"）
 */
function extractComponentId(treeData: any[]): string | null {
  if (!treeData || treeData.length === 0) {
    console.warn("⚠️ [extractComponentId] treeData 为空");
    return null;
  }

  // 尝试从第一个节点中提取
  const firstNode = treeData[0];
  
  if (!firstNode) {
    console.warn("⚠️ [extractComponentId] firstNode 为空");
    return null;
  }

  console.log("🔍 [extractComponentId] 开始提取，firstNode:", {
    id: firstNode.id,
    type: firstNode.type,
    hasDataSource: !!firstNode.dataSource,
    dataSourceId: firstNode.dataSource?.id,
    metadataComponentId: firstNode.dataSource?.metadata?.componentId,
    componentType: firstNode.dataSource?.componentType,
    // 检查是否有子节点（可能是门店节点或思考方式节点）
    hasChildren: !!firstNode.children,
    childrenCount: firstNode.children?.length || 0,
  });

  // 方法1: 从 dataSource.metadata.componentId 中提取（门店组件最可靠）
  const metadataComponentId = firstNode?.dataSource?.metadata?.componentId;
  if (metadataComponentId) {
    console.log("✅ [extractComponentId] 从 metadata.componentId 提取:", metadataComponentId);
    return metadataComponentId;
  } else {
    console.warn("⚠️ [extractComponentId] metadata.componentId 不存在:", {
      hasMetadata: !!firstNode?.dataSource?.metadata,
      metadataKeys: firstNode?.dataSource?.metadata ? Object.keys(firstNode.dataSource.metadata) : [],
      dataSourceKeys: firstNode?.dataSource ? Object.keys(firstNode.dataSource) : [],
    });
  }

  // 方法2: 从 dataSource.id 中提取（需要处理 store:xxx:xxx 格式）
  if (firstNode?.dataSource?.id) {
    const id = firstNode.dataSource.id;
    // 如果是 store:xxx:xxx 格式，需要从 componentType 映射到 componentId
    if (id.startsWith('store:')) {
      // 从 dataSource 中获取 componentType
      const componentType = firstNode.dataSource.componentType;
      console.log("🔍 [extractComponentId] 尝试从 componentType 映射:", {
        id,
        componentType,
        componentTypeType: typeof componentType,
      });
      
      if (componentType) {
        // 组件类型到 componentId 的映射（支持枚举值和字符串）
        // 注意：StoreComponentType 枚举的值是字符串（如 "room-status"）
        const componentIdMap: Record<string, string> = {
          // 枚举值形式（枚举的键）
          "ROOM_STATUS": "table:room-status",
          "ORDER_TRAFFIC": "chart:order-traffic",
          "ROOM_STATUS_RECENTLY": "table:room-status-recently",
          "PRICE_ALERT": "card:price-alert",
          "REVPAR": "chart:revpar",
          "CHANNEL_INFO": "chart:channel-info",
          // 字符串形式（枚举的值）
          "room-status": "table:room-status",
          "order-traffic": "chart:order-traffic",
          "room-status-recently": "table:room-status-recently",
          "price-alert": "card:price-alert",
          "revpar": "chart:revpar",
          "channel-info": "chart:channel-info",
        };
        
        // 尝试多种方式匹配
        let componentId = componentIdMap[componentType] || 
                          componentIdMap[String(componentType)] ||
                          componentIdMap[componentType?.toString()];
        
        if (componentId) {
          console.log("✅ [extractComponentId] 从 componentType 映射到 componentId:", {
            componentType,
            componentId,
          });
          return componentId;
        } else {
          console.warn("⚠️ [extractComponentId] 无法映射 componentType:", {
            componentType,
            availableKeys: Object.keys(componentIdMap),
          });
        }
      } else {
        console.warn("⚠️ [extractComponentId] componentType 为空，id:", id);
      }
    }
    // 如果不是 store: 格式，直接返回（如 stat-card:xxx）
    return id;
  }

  // 方法3: 从节点的 id 中提取（去掉前缀 "data-"）
  if (firstNode?.id) {
    const id = firstNode.id;
    console.log("🔍 [extractComponentId] 尝试从节点 id 提取:", id);
    
    // id 格式可能是 "data-store:123:room-status-0"
    // 需要提取 componentId 部分
    if (id.startsWith('data-store:')) {
      // 从 "data-store:123:room-status-0" 中提取 componentType，然后映射到 componentId
      const match = id.match(/^data-store:(\d+):([^-]+)/);
      if (match?.[2]) {
        const componentType = match[2];
        const componentIdMap: Record<string, string> = {
          "room-status": "table:room-status",
          "order-traffic": "chart:order-traffic",
          "room-status-recently": "table:room-status-recently",
          "price-alert": "card:price-alert",
          "revpar": "chart:revpar",
          "channel-info": "chart:channel-info",
        };
        const componentId = componentIdMap[componentType];
        if (componentId) {
          console.log("✅ [extractComponentId] 从节点 id 提取 componentType 并映射:", {
            componentType,
            componentId,
          });
          return componentId;
        }
      }
    }
    
    // id 格式可能是 "data-stat-card:price-adjustment-stores-0"
    // 需要提取 "stat-card:price-adjustment-stores" 部分
    const match = id.match(/^data-(.+?)(-\d+)?$/);
    if (match?.[1]) {
      const extracted = match[1];
      console.log("✅ [extractComponentId] 从节点 id 提取:", extracted);
      return extracted;
    }
    // 如果格式不匹配，直接返回 id（去掉 "data-" 前缀）
    if (id.startsWith('data-')) {
      const extracted = id.replace(/^data-/, '').replace(/-\d+$/, '');
      console.log("✅ [extractComponentId] 从节点 id 提取（简化）:", extracted);
      return extracted;
    }
  }

  // 方法4: 从 key 中提取
  if (firstNode?.key) {
    console.log("✅ [extractComponentId] 从 key 提取:", firstNode.key);
    return firstNode.key;
  }

  // 方法5: 从 componentId 中提取
  if (firstNode?.componentId) {
    console.log("✅ [extractComponentId] 从 componentId 提取:", firstNode.componentId);
    return firstNode.componentId;
  }

  console.error("❌ [extractComponentId] 所有提取方法都失败，firstNode:", JSON.stringify(firstNode, null, 2));
  return null;
}

/**
 * 从树形数据中提取主要数据和对比数据
 */
function extractDataFromTree(treeData: any[]): {
  primary: any;
  comparison?: any[];
} {
  if (!treeData || treeData.length === 0) {
    return { primary: null };
  }

  // 如果只有一个数据源，只返回 primary
  if (treeData.length === 1) {
    return {
      primary: treeData[0]?.data || treeData[0],
    };
  }

  // 如果有多个数据源，第一个作为 primary，其余作为 comparison
  return {
    primary: treeData[0]?.data || treeData[0],
    comparison: treeData.slice(1).map((node) => node?.data || node),
  };
}

/**
 * 调用 AI 服务（使用后端 API）
 * @param params - AI 调用参数
 * @returns Promise<string> - AI 响应内容
 */
export const callAiService = async (params: CallAiParams): Promise<string> => {
  const { data, thinkingMode } = params;

  if (!thinkingMode) {
    throw new Error("thinkingMode 不能为空");
  }

  if (!data || data.length === 0) {
    throw new Error("data 不能为空");
  }

  console.log("📤 [AI服务] 调用 AI 服务:", {
    thinkingMode: thinkingMode.id,
    dataLength: data.length,
  });

  try {
    // 从树形数据中提取 component_id
    const componentId = extractComponentId(data);
    
    console.log("🔍 [AI服务] 调试信息:", {
      treeDataLength: data.length,
      firstNode: data[0] ? {
        id: data[0].id,
        type: data[0].type,
        hasDataSource: !!data[0].dataSource,
        dataSourceId: data[0].dataSource?.id,
        dataSourceMetadata: data[0].dataSource?.metadata,
        dataSourceComponentType: data[0].dataSource?.componentType,
        dataSourceComponentTypeType: typeof data[0].dataSource?.componentType,
        metadataComponentId: data[0].dataSource?.metadata?.componentId,
        // 完整的数据源对象（用于调试）
        dataSource: data[0].dataSource ? {
          id: data[0].dataSource.id,
          type: data[0].dataSource.type,
          componentType: data[0].dataSource.componentType,
          metadataKeys: data[0].dataSource.metadata ? Object.keys(data[0].dataSource.metadata) : [],
        } : null,
      } : null,
      extractedComponentId: componentId,
      // 完整的树形数据（前 2 层，用于调试）
      treeDataPreview: data.slice(0, 2).map(node => ({
        id: node.id,
        type: node.type,
        label: node.label,
        hasDataSource: !!node.dataSource,
        hasChildren: !!node.children,
        childrenCount: node.children?.length || 0,
      })),
    });
    
    if (!componentId) {
      console.error("❌ [AI服务] 无法提取 componentId，树形数据:", JSON.stringify(data, null, 2));
      throw new Error("无法从数据中提取 component_id");
    }

    // 从树形数据中提取主要数据和对比数据
    const extractedData = extractDataFromTree(data);
    const { primary, comparison } = extractedData;

    console.log("📋 [AI服务] 提取的信息:", {
      componentId,
      hasPrimary: !!primary,
      hasComparison: !!comparison,
      comparisonCount: comparison?.length ?? 0,
    });

    // 调用 msAi 接口
    // 注意：使用接口定义的参数名（驼峰格式）
    const response = await aiConfigApi.analyze({
      componentId: componentId,  // 使用 componentId 而不是 component_id
      thinkingModeId: thinkingMode.id,  // 使用 thinkingModeId 而不是 thinking_mode_id
      data: {
        primary,
        comparison,
      },
    });

    console.log("✅ [AI服务] AI 调用成功，响应长度:", response.result.length);
    return response.result;
  } catch (error: any) {
    console.error("❌ [AI服务] AI 调用失败:", error);
    throw error;
  }
};

/**
 * 获取 AI 服务配置
 * @returns AiServiceConfig | null
 */
export const getAiServiceConfig = (): AiServiceConfig | null => {
  return AiService.getConfig() as AiServiceConfig | null;
};
